import { gql } from 'graphql-tag'

/**
 * Inbox impersonation (cases) — a teammate's case inbox as the caller sees it
 * while mirroring them, and the "Reviewed" write that clears one card from the
 * caller's mirror without touching the teammate's read state. Server:
 * `extended-graphql/inbox-impersonation.{queries,mutations}.ts`.
 *
 * The bar's list of mirror-able teammates reuses `getViewablePeers`
 * (`VIEWABLE_PEERS_QUERY`, `graphql/cases/queries.js`) — no new query for it.
 */

/**
 * Several teammates' case inboxes at once — one group per mirrored teammate, so
 * the bar can multi-select and the page stacks a teal section each (Sandy
 * 2026-09-17). Server: `getImpersonatedCaseInboxes`.
 */
export const IMPERSONATED_CASE_INBOXES_QUERY = gql`
  query GetImpersonatedCaseInboxes($targetUserIds: [ID!]!) {
    getImpersonatedCaseInboxes(targetUserIds: $targetUserIds)
  }
`

/**
 * The STAFF twin (Phase 2) — one group per mirrored teammate, MESSAGES only,
 * each carrying today's Reviewed staff. Server: `getImpersonatedStaffInboxes`.
 */
export const IMPERSONATED_STAFF_INBOXES_QUERY = gql`
  query GetImpersonatedStaffInboxes($targetUserIds: [ID!]!) {
    getImpersonatedStaffInboxes(targetUserIds: $targetUserIds)
  }
`

/** The CONTACT twin (Phase 3). Server: `getImpersonatedContactInboxes`. */
export const IMPERSONATED_CONTACT_INBOXES_QUERY = gql`
  query GetImpersonatedContactInboxes($targetUserIds: [ID!]!) {
    getImpersonatedContactInboxes(targetUserIds: $targetUserIds)
  }
`

/** The MISSED-CALL twin (Phase 4). Server: `getImpersonatedMissedInboxes`. */
export const IMPERSONATED_MISSED_INBOXES_QUERY = gql`
  query GetImpersonatedMissedInboxes($targetUserIds: [ID!]!) {
    getImpersonatedMissedInboxes(targetUserIds: $targetUserIds)
  }
`

/**
 * The mirror-able teammates WITH the caller's exact per-peer total — the bar
 * offers only those with `total > 0` (plus whoever is selected) and hides when
 * none have anything. Replaces the bar's use of `getViewablePeers`, which
 * carried no counts. Server: `getImpersonatablePeers`.
 */
export const IMPERSONATABLE_PEERS_QUERY = gql`
  query GetImpersonatablePeers {
    getImpersonatablePeers
  }
`

export const MARK_CASE_REVIEWED_MUTATION = gql`
  mutation MarkCaseReviewed($targetUserId: ID!, $caseId: ID!) {
    markCaseReviewed(targetUserId: $targetUserId, caseId: $caseId)
  }
`

export const MARK_STAFF_REVIEWED_MUTATION = gql`
  mutation MarkStaffReviewed($targetUserId: ID!, $staffId: ID!) {
    markStaffReviewed(targetUserId: $targetUserId, staffId: $staffId)
  }
`

export const MARK_CONTACT_REVIEWED_MUTATION = gql`
  mutation MarkContactReviewed($targetUserId: ID!, $contactId: ID!) {
    markContactReviewed(targetUserId: $targetUserId, contactId: $contactId)
  }
`

export const MARK_MISSED_REVIEWED_MUTATION = gql`
  mutation MarkMissedReviewed($targetUserId: ID!, $contactId: ID!) {
    markMissedReviewed(targetUserId: $targetUserId, contactId: $contactId)
  }
`
